// Tasarim Motoru — STIL MATRISI.
//
// Bir tasarim = nis (metin + konu) x stil x eksen secimleri:
//   yerlesim (layout) x renk paleti x font cifti x illustrasyon.
// "Beni sasirt" her eksenden rastgele secer; AI YOK (illustrasyon kutuphaneden
// gelir), bu yuzden bedava ve aninda. Kutuphane bos ise ilk illustrasyon AI ile
// uretilir ve EngineAsset'e kaydedilir; sonrakiler oradan.
//
// Koordinatlar URUNUN GUVENLI KUTUSUNA oranla (0..1) — ayni dal daire, kalp,
// kupa sarmasi gibi farkli sablonlara uyarlanir. Metinler Etsy kuralina gore
// Ingilizce.

import { printGeometry, type PrintTemplate, type PrintVariant, type EngineLayer, type EngineImageLayer } from "./print-templates";

let uidSeq = 0;
const engineUid = () => `l${Date.now().toString(36)}${(uidSeq++).toString(36)}${Math.random().toString(36).slice(2, 5)}`;

export type TextRole = "title" | "names" | "date";

export interface EngineNiche {
  key: string;
  label: string;
  group: "event" | "recipient" | "evergreen";
  copy: Record<TextRole, string>;
  /** Illustrasyon istemlerinde kullanilan konu (Ingilizce) */
  subject: string;
  /** Alt nisler: ayni tasarim, yalnizca baslik degisir (grandma -> nana...) */
  aliases?: Array<{ key: string; label: string; title: string }>;
}

export const ENGINE_NICHES: EngineNiche[] = [
  { key: "engaged", label: "Nisanli", group: "event", copy: { title: "Engaged", names: "Mia & Eli", date: "10.20.2026" }, subject: "engagement, diamond ring, champagne, winter romance" },
  { key: "first-christmas-married", label: "Ilk Noel (evli)", group: "event", copy: { title: "Our First Christmas", names: "Mr & Mrs Carter", date: "2026" }, subject: "newlyweds first christmas, wedding rings, mistletoe" },
  { key: "baby-first-christmas", label: "Bebegin ilk Noeli", group: "event", copy: { title: "Baby's First Christmas", names: "Oliver", date: "2026" }, subject: "baby's first christmas, teddy bear, stocking, rattle" },
  { key: "new-home", label: "Yeni ev", group: "event", copy: { title: "Our First Home", names: "The Wilsons", date: "2026" }, subject: "cozy new house, house key, christmas wreath on door" },
  { key: "pet-memorial", label: "Evcil anma", group: "evergreen", copy: { title: "Forever in our hearts", names: "Buster", date: "2012 - 2026" }, subject: "pet memorial, paw prints, angel wings, rainbow bridge" },
  {
    key: "grandma", label: "Buyukanne", group: "recipient", copy: { title: "Grandma", names: "Emma, Noah & Lily", date: "2026" }, subject: "grandmother love, knitting, cozy christmas, flowers",
    aliases: [
      { key: "nana", label: "Nana", title: "Nana" }, { key: "mimi", label: "Mimi", title: "Mimi" },
      { key: "granny", label: "Granny", title: "Granny" }, { key: "gigi", label: "Gigi", title: "Gigi" },
      { key: "oma", label: "Oma", title: "Oma" }, { key: "nonna", label: "Nonna", title: "Nonna" },
      { key: "grammy", label: "Grammy", title: "Grammy" }, { key: "mamaw", label: "Mamaw", title: "Mamaw" },
    ],
  },
  {
    key: "mom", label: "Anne", group: "recipient", copy: { title: "Mom", names: "Love, Ava & Liam", date: "2026" }, subject: "mother's love, flowers, hearts",
    aliases: [
      { key: "mama", label: "Mama", title: "Mama" }, { key: "mommy", label: "Mommy", title: "Mommy" },
      { key: "mum", label: "Mum", title: "Mum" },
    ],
  },
  // Noel serisi 5. bolum: aunt/auntie tisortte satiyor, takida neredeyse yok
  {
    key: "aunt", label: "Teyze/hala", group: "recipient", copy: { title: "Auntie", names: "Love, Ava & Liam", date: "2026" }, subject: "aunt's love, flowers, hearts, cozy christmas",
    aliases: [
      { key: "aunt", label: "Aunt", title: "Aunt" }, { key: "aunty", label: "Aunty", title: "Aunty" },
      { key: "tia", label: "Tia", title: "Tia" }, { key: "titi", label: "Titi", title: "Titi" },
    ],
  },
  { key: "teacher", label: "Ogretmen", group: "recipient", copy: { title: "Best Teacher", names: "Mrs. Johnson", date: "2026" }, subject: "teacher gift, red apple, pencils, books" },
  { key: "best-friends", label: "Arkadaslar", group: "recipient", copy: { title: "Best Friends", names: "Sophie & Grace", date: "2026" }, subject: "friendship, two mugs of cocoa, matching scarves" },
];

/** Yerlesim elemani: guvenli kutuya oranla konum; yazi boyu guvenli kutu yuksekligine oranla */
export interface LayoutElement {
  role: TextRole | "illus";
  x: number;
  y: number;
  w: number;
  /** yalnizca illus: kutu yuksekligi */
  h?: number;
  size?: number;
  font?: "display" | "body";
  caps?: boolean;
  /** illus dikey hizasi */
  valign?: "top" | "center" | "bottom";
  /** illus katman sirasi: arkada (cerceve/celenk) */
  behind?: boolean;
}

export interface EngineLayout {
  key: string;
  label: string;
  elements: LayoutElement[];
}

export const ENGINE_LAYOUTS: EngineLayout[] = [
  {
    key: "illus-bottom", label: "Alt sahne",
    elements: [
      { role: "title", x: 0, y: 0.06, w: 1, size: 0.2, font: "display" },
      { role: "names", x: 0, y: 0.32, w: 1, size: 0.095, font: "body", caps: true },
      { role: "date", x: 0.1, y: 0.45, w: 0.8, size: 0.065, font: "body" },
      { role: "illus", x: 0, y: 0.56, w: 1, h: 0.44, valign: "bottom" },
    ],
  },
  {
    key: "illus-top", label: "Ust sahne",
    elements: [
      { role: "illus", x: 0.1, y: 0, w: 0.8, h: 0.4, valign: "top" },
      { role: "title", x: 0, y: 0.43, w: 1, size: 0.17, font: "display" },
      { role: "names", x: 0, y: 0.65, w: 1, size: 0.085, font: "body", caps: true },
      { role: "date", x: 0.1, y: 0.78, w: 0.8, size: 0.06, font: "body" },
    ],
  },
  {
    key: "wreath", label: "Celenk",
    elements: [
      { role: "illus", x: -0.04, y: -0.04, w: 1.08, h: 1.08, valign: "center", behind: true },
      { role: "title", x: 0.12, y: 0.3, w: 0.76, size: 0.15, font: "display" },
      { role: "names", x: 0.12, y: 0.5, w: 0.76, size: 0.075, font: "body", caps: true },
      { role: "date", x: 0.2, y: 0.62, w: 0.6, size: 0.055, font: "body" },
    ],
  },
  {
    key: "type-stack", label: "Tipografi",
    elements: [
      { role: "title", x: 0, y: 0.2, w: 1, size: 0.24, font: "display" },
      { role: "names", x: 0, y: 0.52, w: 1, size: 0.1, font: "body", caps: true },
      { role: "date", x: 0.15, y: 0.68, w: 0.7, size: 0.065, font: "body" },
    ],
  },
];

export interface EnginePalette { key: string; bg: string; title: string; names: string; date: string; tone: string }
export interface EngineFontPair { key: string; display: string; body: string }

export interface EngineStyle {
  key: string;
  label: string;
  /** Kart onizlemesi icin renk */
  swatch: string;
  layouts: string[];
  palettes: EnginePalette[];
  fonts: EngineFontPair[];
  /** AI illustrasyon istemi; {subject} ve {tone} doldurulur */
  illustrationPrompt: string | null;
  /** Illustrasyon tipi: sahne / celenk-cerceve */
  illustrationKind?: "scene" | "wreath";
}

export const ENGINE_STYLES: EngineStyle[] = [
  {
    key: "coquette", label: "Coquette", swatch: "#f9c6d3",
    layouts: ["illus-top", "wreath"],
    palettes: [
      { key: "blush", bg: "#fff5f7", title: "#c2416b", names: "#5b2a3a", date: "#9d5a6e", tone: "soft blush pink and cream" },
      { key: "rose", bg: "#ffffff", title: "#b83260", names: "#3f1d2b", date: "#b83260", tone: "rose pink and white" },
      { key: "lilac", bg: "#fbf7ff", title: "#8a5cb8", names: "#3d2b52", date: "#8a5cb8", tone: "lilac and pale pink" },
    ],
    fonts: [
      { key: "vibes-playfair", display: "Great Vibes", body: "Playfair Display" },
      { key: "allura-cinzel", display: "Allura", body: "Cinzel" },
      { key: "sacramento-dmserif", display: "Sacramento", body: "DM Serif Display" },
    ],
    illustrationPrompt: "a delicate coquette aesthetic ornamental frame: a round scalloped lace border decorated with dainty satin ribbon bows, tiny pearls and small flowers, small {subject} accent objects, EMPTY center, hand-painted watercolor with fine ink outline, {tone} colors",
    illustrationKind: "wreath",
  },
  {
    key: "scenic", label: "Scenic", swatch: "#9cc3d5",
    layouts: ["illus-bottom", "illus-top"],
    palettes: [
      { key: "winter-blue", bg: "#ffffff", title: "#c2632f", names: "#1f3550", date: "#1f3550", tone: "dusty blue, sage and a touch of burnt orange" },
      { key: "forest", bg: "#ffffff", title: "#2f5d46", names: "#2b2b2b", date: "#2f5d46", tone: "deep forest green and gold" },
      { key: "candy", bg: "#fffaf5", title: "#b33a3a", names: "#2d3a4a", date: "#b33a3a", tone: "christmas red, mint and cream" },
    ],
    fonts: [
      { key: "playfair-cinzel", display: "Playfair Display", body: "Cinzel" },
      { key: "dmserif-montserrat", display: "DM Serif Display", body: "Montserrat" },
      { key: "abril-oswald", display: "Abril Fatface", body: "Oswald" },
    ],
    illustrationPrompt: "a simple decorative border element: a single row of 5 to 7 watercolor pine trees of different heights standing on a thin strip of snow, a few small {subject} accent objects at the base of the trees (objects only, no people), {tone} colors, wide horizontal strip",
    illustrationKind: "scene",
  },
  {
    key: "floral", label: "Floral", swatch: "#e9b9a3",
    layouts: ["wreath", "illus-top"],
    palettes: [
      { key: "wildflower", bg: "#ffffff", title: "#a4483a", names: "#2f2a28", date: "#a4483a", tone: "terracotta, mustard and sage wildflowers" },
      { key: "blue-floral", bg: "#ffffff", title: "#2e4f7a", names: "#22313f", date: "#2e4f7a", tone: "cornflower blue and peach" },
      { key: "blush-floral", bg: "#fffafa", title: "#b4566a", names: "#3b2a2e", date: "#b4566a", tone: "blush pink and eucalyptus green" },
    ],
    fonts: [
      { key: "dancing-cinzel", display: "Dancing Script", body: "Cinzel" },
      { key: "satisfy-playfair", display: "Satisfy", body: "Playfair Display" },
      { key: "vibes-montserrat", display: "Great Vibes", body: "Montserrat" },
    ],
    illustrationPrompt: "elegant watercolor floral wreath forming a round border with an EMPTY center, wildflowers and leaves, {subject} accents, {tone} colors",
    illustrationKind: "wreath",
  },
  {
    key: "minimal", label: "Minimal", swatch: "#d9d4cc",
    layouts: ["type-stack"],
    palettes: [
      { key: "ink", bg: "#ffffff", title: "#111827", names: "#111827", date: "#6b7280", tone: "black" },
      { key: "gold", bg: "#fffdf7", title: "#9a7b2f", names: "#2b2b2b", date: "#9a7b2f", tone: "gold" },
      { key: "navy", bg: "#ffffff", title: "#1e3a5f", names: "#1e3a5f", date: "#64748b", tone: "navy" },
    ],
    fonts: [
      { key: "vibes-cinzel", display: "Great Vibes", body: "Cinzel" },
      { key: "bebas-playfair", display: "Bebas Neue", body: "Playfair Display" },
      { key: "pacifico-montserrat", display: "Pacifico", body: "Montserrat" },
    ],
    illustrationPrompt: null,
  },
];

/** Stil dali: her eksenin secimi (belgede saklanir, "Beni sasirt" yeniden secer) */
export interface StyleBranch {
  niche: string;
  /** Alt nis (grandma -> nana) — baslik bunu kullanir */
  alias?: string | null;
  style: string;
  layout: string;
  palette: string;
  font: string;
  /** Kutuphaneden secilen illustrasyon (EngineAsset id + url + boyut) */
  illus?: { id: string; url: string; w: number; h: number } | null;
}

export function engineStyle(key: string): EngineStyle | undefined {
  return ENGINE_STYLES.find((s) => s.key === key);
}
export function engineNiche(key: string): EngineNiche | undefined {
  return ENGINE_NICHES.find((n) => n.key === key);
}

/** AI illustrasyon istemi (baskiya uygun: tek parca, yazisiz, duz yesil zemin) */
export function illustrationPrompt(style: EngineStyle, niche: EngineNiche, palette: EnginePalette): string | null {
  if (!style.illustrationPrompt) return null;
  const body = style.illustrationPrompt.replace("{subject}", niche.subject).replace("{tone}", palette.tone);
  return `${body}. ISOLATED design element for a personalized gift print: NO sky, NO landscape or room background, NO people, NO text, NO letters, NO numbers.

PRINT RULES: COMPLETELY FLAT, SOLID, UNIFORM background in PURE CHROMA GREEN #00FF00 (if the art itself needs bright green, use pure magenta #FF00FF instead); ONLY the illustration, nothing else.`;
}

/** Rastgele dal (illustrasyon haric — o kutuphaneden secilir) */
export function randomBranch(niche: string, styleKey?: string, rnd: () => number = Math.random): StyleBranch {
  const pick = <T,>(a: T[]) => a[Math.floor(rnd() * a.length)];
  const style = styleKey ? engineStyle(styleKey) ?? ENGINE_STYLES[0] : pick(ENGINE_STYLES);
  return {
    niche,
    style: style.key,
    layout: pick(style.layouts),
    palette: pick(style.palettes).key,
    font: pick(style.fonts).key,
    illus: null,
  };
}

// ---- Daldan katmanlar (editor + sunucu ortak) ----


/** Dali bir varyantin guvenli kutusuna yerlestirir. Kullanicinin degistirdigi
 *  metinler (ayni rol) korunur; yalnizca yerlesim/renk/font degisir. */
// Kalp (100x100, alan sekilleriyle ayni egriler) — kiris olcumu icin ornekleme
const HEART_SEGS: Array<[number, number, number, number, number, number]> = [
  [22, 78, 0, 58, 0, 30], [0, 13, 13, 0, 28, 0], [39, 0, 46, 6, 50, 15],
  [54, 6, 61, 0, 72, 0], [87, 0, 100, 13, 100, 30], [100, 58, 78, 78, 50, 98],
];
let heartPoly: Array<[number, number]> | null = null;
function heartPolygon(): Array<[number, number]> {
  if (heartPoly) return heartPoly;
  const pts: Array<[number, number]> = [];
  let [px, py] = [50, 98];
  for (const [a, b, c, d, e, f] of HEART_SEGS) {
    for (let i = 1; i <= 40; i++) {
      const t = i / 40, u = 1 - t;
      pts.push([u * u * u * px + 3 * u * u * t * a + 3 * u * t * t * c + t * t * t * e, u * u * u * py + 3 * u * u * t * b + 3 * u * t * t * d + t * t * t * f]);
    }
    [px, py] = [e, f];
  }
  return (heartPoly = pts.map(([x, y]) => [x / 100, y / 100]));
}

/** Seklin y (0..1, kesim kutusuna oranla) yuksekligindeki ic genisligi: [sol, sag] 0..1 */
export function shapeSpan(shape: "rect" | "ellipse" | "heart", y: number): [number, number] | null {
  if (y < 0 || y > 1) return null;
  if (shape === "rect") return [0, 1];
  if (shape === "ellipse") {
    const k = 1 - (2 * y - 1) ** 2;
    if (k <= 0) return null;
    const half = Math.sqrt(k) / 2;
    return [0.5 - half, 0.5 + half];
  }
  // Kalp: ust kisimda iki lob arasi bosluk olur -> en dis sinirlar
  const poly = heartPolygon();
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i], [x2, y2] = poly[(i + 1) % poly.length];
    if ((y1 <= y && y2 > y) || (y2 <= y && y1 > y)) {
      const x = x1 + ((y - y1) / (y2 - y1)) * (x2 - x1);
      if (x < lo) lo = x; if (x > hi) hi = x;
    }
  }
  return lo < hi ? [lo, hi] : null;
}

/** Dali bir varyanta yerlestirir. Yerlesim, URUNUN SEKLINE uyar: her satir o
 *  yukseklikteki gercek genislige (kiris) gore daralir/kuculur; icerik delikten
 *  asagida baslar. Kullanicinin degistirdigi metinler (ayni rol) korunur. */
export function buildFromBranch(branch: StyleBranch, t: PrintTemplate, v: PrintVariant, prev: EngineLayer[], retitle = false): EngineLayer[] {
  const style = engineStyle(branch.style) ?? ENGINE_STYLES[0];
  const niche = engineNiche(branch.niche) ?? ENGINE_NICHES[0];
  const layout = ENGINE_LAYOUTS.find((l) => l.key === branch.layout) ?? ENGINE_LAYOUTS[0];
  const pal = style.palettes.find((p) => p.key === branch.palette) ?? style.palettes[0];
  const fp = style.fonts.find((f) => f.key === branch.font) ?? style.fonts[0];
  const { W, H, trim, safe, hole } = printGeometry(t, v);
  const alias = niche.aliases?.find((a) => a.key === branch.alias);
  const kept: Partial<Record<TextRole, string>> = {};
  for (const l of prev) if (l.type === "text" && l.role) kept[l.role] = l.text;
  if (retitle) delete kept.title; // alt nis secildi: baslik yeniden

  // Icerik bandi: guvenli kutu; delik varsa deligin (koruma halkasiyla) altindan
  const inset = safe.x - trim.x;
  // Seklin yeterince genis oldugu (kesim genisliginin %45'i) dikey aralik:
  // daire ust/alt kenarlari, kalbin sivri ucu bant disinda kalir
  let wideTop = 0, wideBottom = 1;
  for (let i = 0; i <= 100; i++) { const sp = shapeSpan(v.shape, i / 100); if (sp && sp[1] - sp[0] >= 0.45) { wideTop = i / 100; break; } }
  for (let i = 100; i >= 0; i--) { const sp = shapeSpan(v.shape, i / 100); if (sp && sp[1] - sp[0] >= 0.45) { wideBottom = i / 100; break; } }
  const top = Math.max(safe.y, trim.y + wideTop * trim.h, hole ? hole.y + hole.r * 1.8 : -Infinity);
  const bottom = Math.min(safe.y + safe.h, trim.y + wideBottom * trim.h);
  const bandH = Math.max(1, bottom - top);
  /** px yukseklik araligindaki en dar kullanilabilir genislik (guvenli pay dusulmus) */
  const usable = (y0: number, y1: number): { x: number; w: number } => {
    let lo = -Infinity, hi = Infinity;
    for (let i = 0; i <= 6; i++) {
      const yy = y0 + ((y1 - y0) * i) / 6;
      const sp = shapeSpan(v.shape, (yy - trim.y) / trim.h);
      if (!sp) return { x: trim.x + trim.w / 2, w: 0 };
      lo = Math.max(lo, trim.x + sp[0] * trim.w + inset);
      hi = Math.min(hi, trim.x + sp[1] * trim.w - inset);
    }
    return { x: lo, w: Math.max(0, hi - lo) };
  };

  const out: EngineLayer[] = [];
  const behind: EngineLayer[] = [];
  for (const e of layout.elements) {
    const ey = top + e.y * bandH;
    const wantW = e.w * safe.w;
    if (e.role === "illus") {
      if (!branch.illus) continue;
      const bh = (e.h ?? 0.4) * bandH;
      // Celenk/cerceve sekli kaplar; sahne ise bandinin ortasindaki kirise sigar
      const span = e.behind ? { x: safe.x + e.x * safe.w, w: wantW } : usable(ey + bh * 0.35, ey + bh * 0.65);
      const bw = Math.min(wantW, span.w);
      const ar = branch.illus.w / Math.max(1, branch.illus.h);
      let iw = bw, ih = iw / ar;
      if (ih > bh) { ih = bh; iw = ih * ar; }
      const cx = e.behind ? safe.x + (e.x + e.w / 2) * safe.w : span.x + span.w / 2;
      const iy = e.valign === "top" ? ey : e.valign === "center" ? ey + (bh - ih) / 2 : ey + bh - ih;
      const layer: EngineImageLayer = { id: engineUid(), type: "image", url: branch.illus.url, x: (cx - iw / 2) / W, y: iy / H, w: iw / W, h: ih / H, rotation: 0, role: "illus" };
      (e.behind ? behind : out).push(layer);
      continue;
    }
    const role = e.role;
    const text = kept[role] ?? (role === "title" && alias ? alias.title : niche.copy[role]);
    const size0 = (e.size ?? 0.08) * bandH;
    const span = usable(ey, ey + size0 * 1.05);
    // Dar kiriste yazi orantili kuculur (cok dar ise en az %55)
    const k = span.w > 0 ? Math.max(0.55, Math.min(1, span.w / wantW)) : 0.55;
    const size = size0 * k;
    const bw = Math.min(wantW, span.w || wantW * 0.55);
    const cx = span.w > 0 ? span.x + span.w / 2 : trim.x + trim.w / 2;
    out.push({
      id: engineUid(), type: "text", role, text, x: (cx - bw / 2) / W, y: (ey + (size0 - size) / 2) / H, w: bw / W, rotation: 0,
      fontFamily: e.font === "display" ? fp.display : fp.body,
      fontSize: size / H,
      color: role === "title" ? pal.title : role === "names" ? pal.names : pal.date,
      align: "center", letterSpacing: e.caps ? 0.08 : 0, lineHeight: 1.05, uppercase: !!e.caps,
      personalize: role === "names" ? "names" : role === "date" ? "date" : null,
    });
  }
  return [...behind, ...out];
}
